import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, Space } from 'antd';
import { CameraOutlined, StopOutlined, ScanOutlined } from '@ant-design/icons';

// jscanify и opencv.js загружаются через CDN в index.html
declare global {
  interface Window {
    cv: any;
    jscanify: any;
  }
}

interface Props {
  onCapture: (blob: Blob) => void;
}

export interface DocScannerHandle {
  restart: () => Promise<void>;
}

const SCANNER_ID = 'doc-scanner-viewport';

const DocScanner = forwardRef<DocScannerHandle, Props>(({ onCapture }, ref) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jscanifyReady, setJscanifyReady] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const scannerRef = useRef<any>(null);

  // Проверяем загрузку jscanify (для подсветки границ)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let attempts = 0;
    const check = () => {
      attempts++;
      if (typeof window.cv !== 'undefined' && typeof window.jscanify !== 'undefined') {
        setJscanifyReady(true);
      } else if (attempts < 60) {
        setTimeout(check, 500);
      }
    };
    setTimeout(check, 500);
  }, []);

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.remove();
      videoRef.current = null;
    }
    scannerRef.current = null;
    setScanning(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;

      const video = document.createElement('video');
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      video.srcObject = stream;
      await video.play();
      videoRef.current = video;

      const container = document.getElementById(SCANNER_ID);
      if (container) {
        container.innerHTML = '';
        video.style.width = '100%';
        video.style.height = 'auto';
        video.style.display = 'block';
        container.appendChild(video);
      }

      // jscanify только для extractPaper (не для real-time подсветки — тормозит)
      if (jscanifyReady && window.jscanify) {
        scannerRef.current = new window.jscanify();
      }

      setScanning(true);
    } catch (e: any) {
      console.error('Camera error:', e);
      setError(e?.message || 'Не удалось запустить камеру');
    }
  }, [jscanifyReady]);

  const doCapture = useCallback(() => {
    if (!videoRef.current) {
      setError('Видео не инициализировано');
      return;
    }
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Мгновенный capture — без jscanify extractPaper (он блокирует UI на 1-2 сек)
    canvas.toBlob((blob) => {
      if (blob) onCapture(blob);
      else setError('toBlob вернул null');
    }, 'image/jpeg', 0.92);
  }, [onCapture]);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  useImperativeHandle(ref, () => ({
    restart: async () => {
      await stop();
      await start();
    },
  }), [stop, start]);

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        {!scanning ? (
          <Button type="primary" icon={<CameraOutlined />} onClick={start} size="large">
            Сканировать
          </Button>
        ) : (
          <>
            <Button danger icon={<StopOutlined />} onClick={stop}>
              Остановить
            </Button>
            <Button type="primary" icon={<ScanOutlined />} onClick={doCapture}>
              Сделать скан
            </Button>
          </>
        )}
      </Space>

      {error && (
        <div style={{ color: '#ff4d4f', marginBottom: 12, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {!jscanifyReady && scanning && (
        <div style={{ color: '#faad14', marginBottom: 12 }}>
          Загрузка библиотек обработки...
        </div>
      )}

      <div
        id={SCANNER_ID}
        style={{
          width: '100%',
          maxWidth: 720,
          border: `3px solid ${error ? '#ff4d4f' : scanning ? '#52c41a' : '#d9d9d9'}`,
          borderRadius: 8,
          overflow: 'hidden',
          transition: 'border-color 0.25s ease',
          minHeight: scanning ? 200 : 0,
        }}
      />
    </div>
  );
});

DocScanner.displayName = 'DocScanner';

export default DocScanner;
